import React, { useState, useEffect, useCallback } from 'react';
import config from "../common/Config";
import { toast } from "sonner";
import { pushQuickBooksMaster, resolveQuickBooksEnabled } from "../../utils/quickBooksTenant";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  IconButton,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Skeleton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify/iconify';
import '../common/commonStyles.css';
import {
  postGetProductCategories,
  axiosPostProductCategoriesForm,
} from '../../utils/productCategoriesApi';

const getCategoryImageUrl = (row) =>
  row?.image || row?.category_image || row?.image_url || row?.img || '';

const Product_Category = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);
  const [totalCategories, setTotalCategories] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [menuAnchor, setMenuAnchor] = useState({ el: null, row: null });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  const [showQuickBooks, setShowQuickBooks] = useState(false);
  const [pushingQb, setPushingQb] = useState(false);
  const token = localStorage.getItem("token");
  
  const [catFormData, setCatFormData] = useState({
    category_code: '',
    category_name: '',
    sort_number: '',
    slug: '',
    description: '',
  });

  const [catEditData, setCatEditData] = useState({
    category_code: '',
    category_name: '',
    sort_number: '',
    slug: '',
    description: '',
    catid: '',
    old_image: '',
  });
  const [addImagePreview, setAddImagePreview] = useState('');
  const [editImagePreview, setEditImagePreview] = useState('');

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const json = await postGetProductCategories(`${config.API_URL}/get_product_categories`, token, {
        draw: page + 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchValue || '',
      });

      if (json?.data && Array.isArray(json.data)) {
        setCategories(json.data);
        setTotalCategories(json.recordsTotal || json.recordsFiltered || json.data.length);
      } else {
        setCategories([]);
        setTotalCategories(0);
      }
    } catch (error) {
      toast.error("Failed to fetch categories");
      setCategories([]);
      setTotalCategories(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, accessToken, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    resolveQuickBooksEnabled().then(setShowQuickBooks);
  }, []);

  const handleCatChange = (e) => {
    const { name, value } = e.target;
    if (name === "category_code" && value < 0) {
      return;
    }
    if (name === "sort_number" && value < 0) {
      return;
    }
    setCatFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCatEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "category_code" && value < 0) {
      return;
    }
    if (name === "sort_number" && value < 0) {
      return;
    }
    setCatEditData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCategorySubmit = async (event) => {
    if (event) {
      event.preventDefault();
    }

    const formData = new FormData();
    formData.append('category_code', catFormData.category_code);
    formData.append('category_name', catFormData.category_name);
    formData.append('sort_number', catFormData.sort_number);
    formData.append('slug', catFormData.slug);
    formData.append('description', catFormData.description);
    const addInput = document.getElementById('category_image_input');
    if (addInput && addInput.files && addInput.files[0]) {
      formData.append('image', addInput.files[0]);
      formData.append('category_image', addInput.files[0]);
    }

    const apiUrl = `${config.API_URL}/add_product_categories`;
    try {
      const data = await axiosPostProductCategoriesForm(apiUrl, token, formData);
      const msg = data.message;
      if (data.success == true) {
        toast.success("Category added successfully.");
        setAddCategoryDialogOpen(false);
        setCatFormData({
          category_code: '',
          category_name: '',
          sort_number: '',
          slug: '',
          description: ''
        });
        setAddImagePreview('');
        const addInput = document.getElementById('category_image_input');
        if (addInput) addInput.value = '';
        fetchData();
      } else {
        toast.error(msg || "Failed to add category");
      }
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to add category");
      }
    }
  };

  const handleCategoryUpdate = async (event) => {
    if (event) {
      event.preventDefault();
    }

    const formData = new FormData();
    formData.append('category_code', catEditData.category_code);
    formData.append('category_name', catEditData.category_name);
    formData.append('sort_number', catEditData.sort_number);
    formData.append('slug', catEditData.slug);
    formData.append('description', catEditData.description);
    formData.append('catid', catEditData.catid);
    formData.append('old_image', catEditData.old_image || '');
    const editInput = document.getElementById('category_image_input_edit');
    if (editInput && editInput.files && editInput.files[0]) {
      formData.append('image', editInput.files[0]);
      formData.append('category_image', editInput.files[0]);
    }

    const apiUrl = `${config.API_URL}/edit_product_categories`;
    try {
      const data = await axiosPostProductCategoriesForm(apiUrl, token, formData);
      const msg = data.message;
      if (data.success) {
        toast.success('Category updated successfully.');
        setEditCategoryDialogOpen(false);
        setCatEditData({
          category_code: '',
          category_name: '',
          sort_number: '',
          slug: '',
          description: '',
          catid: '',
          old_image: '',
        });
        setEditImagePreview('');
        const editInput = document.getElementById('category_image_input_edit');
        if (editInput) editInput.value = '';
        fetchData();
      } else {
        toast.error(msg || 'Failed to update category');
      }
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to update category");
      }
    }
  };

  const handleChangeStatus = async (id, status) => {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('catid', id);

    const apiUrl = `${config.API_URL}/update_categories_status`;
    try {
      await axiosPostProductCategoriesForm(apiUrl, accessToken, formData);
      toast.success('Status changed successfully.');
      fetchData();
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to change status");
      }
    }
  };

  const handleEditClick = (rowData) => {
    const existingImage = getCategoryImageUrl(rowData);
    setCatEditData({
      category_code: rowData.code,
      category_name: rowData.name,
      sort_number: rowData.sort_number,
      slug: rowData.slug,
      description: rowData.description,
      catid: rowData.id,
      old_image: existingImage,
    });
    setEditImagePreview(existingImage || '');
    setEditCategoryDialogOpen(true);
  };

  const handleAddImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (addImagePreview && addImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(addImagePreview);
    }
    setAddImagePreview(URL.createObjectURL(file));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (editImagePreview && editImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveAddImage = (e) => {
    if (e) e.preventDefault();
    if (addImagePreview && addImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(addImagePreview);
    }
    setAddImagePreview('');
    const addInput = document.getElementById('category_image_input');
    if (addInput) addInput.value = '';
  };

  const handleRemoveEditImage = (e) => {
    if (e) e.preventDefault();
    if (editImagePreview && editImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview('');
    setCatEditData((prev) => ({ ...prev, old_image: '' }));
    const editInput = document.getElementById('category_image_input_edit');
    if (editInput) editInput.value = '';
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, row: null });
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, row });
  };

  const handleMenuAction = (action, row) => {
    handleMenuClose();
    if (action === 'edit') {
      handleEditClick(row);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handlePushQuickBooks = async () => {
    if (pushingQb) return;
    setPushingQb(true);
    try {
      const data = await pushQuickBooksMaster("categories");
      toast.success(data.message || data.msg || "Categories pushed to QuickBooks");
    } catch (e) {
      toast.error(e.message || "Push failed");
    } finally {
      setPushingQb(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleCloseAddDialog = () => {
    setAddCategoryDialogOpen(false);
    setCatFormData({
      category_code: '',
      category_name: '',
      sort_number: '',
      slug: '',
      description: '',
    });
    if (addImagePreview && addImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(addImagePreview);
    }
    setAddImagePreview('');
    const addInput = document.getElementById('category_image_input');
    if (addInput) addInput.value = '';
  };

  const handleCloseEditDialog = () => {
    setEditCategoryDialogOpen(false);
    setCatEditData({
      category_code: '',
      category_name: '',
      sort_number: '',
      slug: '',
      description: '',
      catid: '',
      old_image: '',
    });
    if (editImagePreview && editImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview('');
    const editInput = document.getElementById('category_image_input_edit');
    if (editInput) editInput.value = '';
  };

  return (
    <Box>
      <Dialog
        open={addCategoryDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Product Category
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="addNewPdtCategory" onSubmit={handleCategorySubmit} sx={{ pt: 2 }}>
            <Typography variant="body2" className='commonColor' sx={{ mb: 2}}>
              Please fill in the information below. The field labels marked with <span style={{ color: 'red' }}>*</span> are required input fields.
            </Typography>
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Category Code"
                  name="category_code"
                  id="code"
                  value={catFormData.category_code}
                  onChange={handleCatChange}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Category Name"
                  name="category_name"
                  id="name"
                  value={catFormData.category_name}
                  onChange={handleCatChange}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Sort Number"
                  name="sort_number"
                  id="sortNumber"
                  type="number"
                  value={catFormData.sort_number}
                  onChange={handleCatChange}
                  inputProps={{
                    style: {
                      appearance: "none",
                      MozAppearance: "textfield",
                    }
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Description"
                  name="description"
                  id="description"
                  value={catFormData.description}
                  onChange={handleCatChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Category Image
                </Typography>
                <Box sx={{ position: 'relative', width: 88, height: 88 }}>
                  <Box
                    component="label"
                    htmlFor="category_image_input"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 88,
                      height: 88,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px dashed rgba(145, 158, 171, 0.48)',
                      bgcolor: '#f4f6f8',
                    }}
                  >
                    <input
                      type="file"
                      id="category_image_input"
                      name="image"
                      accept="image/png, image/jpeg, image/jpg"
                      hidden
                      onChange={handleAddImageChange}
                    />
                    {addImagePreview ? (
                      <Box
                        component="img"
                        src={addImagePreview}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Iconify icon="mdi:camera-plus-outline" width={28} sx={{ color: '#637381' }} />
                    )}
                  </Box>
                  {addImagePreview ? (
                    <IconButton
                      size="small"
                      onClick={handleRemoveAddImage}
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 22,
                        height: 22,
                        bgcolor: 'error.main',
                        color: '#fff',
                        '&:hover': { bgcolor: 'error.dark' },
                      }}
                    >
                      <Iconify icon="mdi:close" width={14} />
                    </IconButton>
                  ) : null}
                </Box>
              </Grid2>
            </Grid2>
            <input type="hidden" name="slug" id="slug" value={catFormData.slug} onChange={handleCatChange} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAddDialog} className="button-variant-outlined" 
          variant='outlined'
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            className="button-variant-contained"
            form="addNewPdtCategory"
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editCategoryDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Category
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="editBrand" onSubmit={handleCategoryUpdate} sx={{ pt: 1 }}>
            <Typography variant="body2" className="commonColor" sx={{ mb: 2}}>
              Please fill in the information below. The field labels marked with <span style={{ color: 'red' }}>*</span> are required input fields.
            </Typography>
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Category Code"
                  name="category_code"
                  id="codeEdit"
                  value={catEditData.category_code}
                  onChange={handleCatEditChange}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Category Name"
                  name="category_name"
                  id="nameEdit"
                  value={catEditData.category_name}
                  onChange={handleCatEditChange}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Sort Number"
                  name="sort_number"
                  id="sortNumberEdit"
                  type="number"
                  value={catEditData.sort_number}
                  onChange={handleCatEditChange}
                  inputProps={{
                    min: 0,
                    style: {
                      appearance: "none",
                      MozAppearance: "textfield",
                    }
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Description"
                  name="description"
                  id="descriptionEdit"
                  value={catEditData.description}
                  onChange={handleCatEditChange}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Category Image
                </Typography>
                <Box sx={{ position: 'relative', width: 88, height: 88 }}>
                  <Box
                    component="label"
                    htmlFor="category_image_input_edit"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 88,
                      height: 88,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px dashed rgba(145, 158, 171, 0.48)',
                      bgcolor: '#f4f6f8',
                    }}
                  >
                    <input
                      type="file"
                      id="category_image_input_edit"
                      name="image"
                      accept="image/png, image/jpeg, image/jpg"
                      hidden
                      onChange={handleEditImageChange}
                    />
                    {editImagePreview ? (
                      <Box
                        component="img"
                        src={editImagePreview}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Iconify icon="mdi:camera-plus-outline" width={28} sx={{ color: '#637381' }} />
                    )}
                  </Box>
                  {editImagePreview ? (
                    <IconButton
                      size="small"
                      onClick={handleRemoveEditImage}
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 22,
                        height: 22,
                        bgcolor: 'error.main',
                        color: '#fff',
                        '&:hover': { bgcolor: 'error.dark' },
                      }}
                    >
                      <Iconify icon="mdi:close" width={14} />
                    </IconButton>
                  ) : null}
                </Box>
              </Grid2>
            </Grid2>
            <input type="hidden" name="slug" id="slugEdit" value={catEditData.slug} onChange={handleCatEditChange} />
            <input type="hidden" name="catid" value={catEditData.catid} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEditDialog}
          
          variant="outlined"
          className="button-variant-outlined"
          sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="button-variant-contained"
            type="submit"
            form="editBrand"
          >
            Edit Category
          </Button>
        </DialogActions>
      </Dialog>
      <Box className="page-container">
        <Paper className='paper-style'>
          <Box sx={{px:2, py:1.2 }}>
            <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Grid2 size={{ xs: 12, md: 4 }}>
              <Typography className='page-title'>
                Product Category
              </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 8 }} gap={1} display="flex" flexDirection={{ xs: "column", md: "row"}} alignItems={{ md: "center" }} justifyContent="flex-end" flexWrap="nowrap" mt={{ xs: 2, md: 0 }}>
                <TextField
                  className='input-border'
                  size="small"
                  placeholder="Search categories..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    width: { xs: '100%', md: 220 },
                    minWidth: { md: 180 },
                    flexShrink: 1,
                  }}
                />
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={() => setAddCategoryDialogOpen(true)}
                  startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Add Product Category
                </Button>
                {showQuickBooks && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={pushingQb}
                    onClick={handlePushQuickBooks}
                    sx={{
                      color: "#000",
                      borderColor: "#000",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      height: 34,
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {pushingQb ? "Pushing..." : "Push to QuickBooks"}
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer >
            <Table
              size="medium"
              className="table-cell-border"
              sx={{
                tableLayout: "auto",
              }}
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  {["#", "Image", "Category Code", "Category Name", "Status", "Action"].map((header) => {
                    let align = "left";
                    if (header === "#" || header === "Image" || header === "Status" || header === "Action") {
                      align = "center";
                    }
                    return (
                      <TableCell
                        key={header}
                        align={align}
                        className="table-cell-header"
                      >
                        {header}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={6}>
                        <Skeleton height={40} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      className="table-cell-style"
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)"
                        },
                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        {getCategoryImageUrl(row) ? (
                          <Box
                            component="img"
                            src={getCategoryImageUrl(row)}
                            alt=""
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '1px solid rgba(145, 158, 171, 0.24)',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: '#f4f6f8',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(145, 158, 171, 0.24)',
                            }}
                          >
                            <Iconify icon="mdi:image-outline" width={16} sx={{ color: '#637381' }} />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {row.code}
                      </TableCell>
                      <TableCell align="left">
                        {row.name}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          component="label"
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            alignItems: 'center',
                            width: 70,
                            height: 26,
                            cursor: 'pointer',
                            margin: '0 auto',
                          }}
                        >
                          <Box
                            component="input"
                            type="checkbox"
                            checked={row.status == "1"}
                            onChange={(e) => handleChangeStatus(row.id, e.target.checked ? 1 : 0)}
                            sx={{
                              opacity: 0,
                              width: 0,
                              height: 0,
                              position: 'absolute',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'relative',
                              width: '100%',
                              height: '100%',
                              backgroundColor: row.status == "1" ? '#F8991D' : '#eceeef',
                              borderRadius: '13px',
                              transition: 'all 0.3s ease',
                              boxShadow: row.status == "1"
                                ? 'inset 0 1px 2px rgba(0, 0, 0, 0.15), inset 0 0 3px rgba(0, 0, 0, 0.2)'
                                : 'inset 0 1px 2px rgba(0, 0, 0, 0.12), inset 0 0 2px rgba(0, 0, 0, 0.15)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                width: 18,
                                height: 18,
                                left: row.status == "1" ? 'calc(100% - 24px)' : '3px',
                                top: '3px',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '50%',
                                transition: 'all 0.3s ease',
                                boxShadow: '1px 1px 5px rgba(0, 0, 0, 0.2)',
                                zIndex: 2,
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '8px',
                                transform: 'translateY(-50%)',
                                color: '#FFFFFF',
                                fontSize: '9px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                opacity: row.status == "1" ? 1 : 0,
                                transition: 'opacity 0.3s ease',
                                textShadow: '0 1px rgba(0, 0, 0, 0.2)',
                                zIndex: 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ACTIVE
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                right: '8px',
                                transform: 'translateY(-50%)',
                                color: '#aaaaaa',
                                fontSize: '9px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                opacity: row.status == "1" ? 0 : 1,
                                transition: 'opacity 0.3s ease',
                                textShadow: '0 1px rgba(255, 255, 255, 0.5)',
                                zIndex: 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              INACTIVE
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, row)}
                          sx={{
                            color: "#637381",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)"
                            }
                          }}
                        >
                          <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {categories.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={totalCategories}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
          sx: {
            minWidth: 150,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
          },
        }}
      >
        {menuAnchor.row && (
          <MenuItem onClick={() => handleMenuAction('edit', menuAnchor.row)}>
            <ListItemIcon>
              <Iconify icon="fa-regular:edit" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
      </Menu>

    </Box>
  );
};

export default Product_Category;
